import React, { useMemo, useState } from 'react';
import { useApiClient, useFetchImmutable } from '@marageti/z4-lib';
import { TeamMember, TravelerNote, TravelerNoteStatus } from '@marageti/z4-sdk/lib/people';
import { RoleName } from '@marageti/z4-sdk/lib/auth';
import { useAuthStore } from '../../store';
import TravelerProfileNoteModal from './traveler-profile-note-modal';
import TravelerProfileNotesSectionView from './traveler-profile-notes-section-view';
import useTravelerProfileNotes from './use-traveler-profile-notes';

type TravelerProfileNotesSectionProps = {
  travelerId: string;
};

function compareTravelerNotesByUpdatedAtDesc(a: TravelerNote, b: TravelerNote) {
  return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
}

const TravelerProfileNotesSection = ({
  travelerId,
}: TravelerProfileNotesSectionProps) => {
  const apiClient = useApiClient();
  const { data: teamMembers, isLoading: isTeamMembersLoading } = useFetchImmutable<TeamMember[]>(
    apiClient.peopleClient.getTeamMembers,
    [{ params: { limit: 10000 } }],
    true,
  );
  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    teamMembers?.forEach((tm) => m.set(tm.id, tm.fullName));
    return m;
  }, [teamMembers]);

  const { loggedInTeamMember } = useAuthStore();
  const teamMemberIdForNew = loggedInTeamMember?.id ?? 'unknown';
  const canEdit = loggedInTeamMember?.userInfo?.[0].roles?.some(
    (role) => role === RoleName.ADMIN || role === RoleName.CARE,
  ) ?? false;

  const {
    travelerProfile,
    notes,
    addNote,
    updateNote,
    setNoteStatus,
    isSaving,
  } = useTravelerProfileNotes({
    travelerId,
    teamMemberId: teamMemberIdForNew,
  });

  const [addOpen, setAddOpen] = useState(false);
  const [editNote, setEditNote] = useState<TravelerNote | null>(null);

  const activeNotes = useMemo(
    () => notes.filter((n) => n.status === TravelerNoteStatus.NOT_ARCHIVED).sort(compareTravelerNotesByUpdatedAtDesc),
    [notes],
  );
  const archivedNotes = useMemo(
    () => notes.filter((n) => n.status === TravelerNoteStatus.ARCHIVED).sort(compareTravelerNotesByUpdatedAtDesc),
    [notes],
  );

  const handleArchive = (note: TravelerNote) => setNoteStatus(note.id, TravelerNoteStatus.ARCHIVED);
  const handleRestore = (note: TravelerNote) => setNoteStatus(note.id, TravelerNoteStatus.NOT_ARCHIVED);
  const handleEditFromMenu = (note: TravelerNote) => setEditNote(note);

  return (
    <>
      <TravelerProfileNotesSectionView
        isProfileLoading={!travelerProfile}
        activeNotes={activeNotes}
        archivedNotes={archivedNotes}
        nameById={nameById}
        isMemberNamesLoading={isTeamMembersLoading}
        isSaving={isSaving}
        canEdit={canEdit}
        onAddClick={() => setAddOpen(true)}
        onEdit={handleEditFromMenu}
        onArchive={handleArchive}
        onRestore={handleRestore}
      />

      <TravelerProfileNoteModal
        open={addOpen}
        mode="add"
        onClose={() => setAddOpen(false)}
        onSubmit={(values) => {
          addNote({
            title: values.title,
            content: values.note,
            createdBy: teamMemberIdForNew,
            updatedBy: teamMemberIdForNew,
            status: TravelerNoteStatus.NOT_ARCHIVED,
          });
        }}
      />

      <TravelerProfileNoteModal
        open={Boolean(editNote)}
        mode="edit"
        note={editNote}
        onClose={() => setEditNote(null)}
        onSubmit={(values) => {
          if (editNote) {
            updateNote(editNote.id, {
              title: values.title,
              content: values.note,
              updatedBy: teamMemberIdForNew,
            });
          }
        }}
      />
    </>
  );
};

export default TravelerProfileNotesSection;
